'use client';

import type { CSSProperties, ReactNode } from 'react';
import { FOUNDING_PRICING, formatMarketingUsd } from '@loamify/core-domain';
import { cn } from '../../lib/utils';

export type AuthHeroVariant =
  | 'social-login'
  | 'studio-login'
  | 'two-factor'
  | 'signup-member'
  | 'signup-creator'
  | 'verify-email'
  | 'forgot-password'
  | 'password-reset'
  | 'success'
  | 'creator-plan'
  | 'creator-checkout'
  | 'creator-activating'
  | 'admin-login'
  | 'admin-verify'
  | 'admin-2fa';

export function resolveAuthHeroVariant(stepKey: string): AuthHeroVariant {
  if (stepKey.includes('two-factor') || stepKey.includes('login-2fa')) {
    return stepKey.startsWith('admin') ? 'admin-2fa' : 'two-factor';
  }
  if (stepKey.startsWith('admin-login')) {
    if (stepKey.includes('email-verify')) return 'admin-verify';
    return 'admin-login';
  }
  if (stepKey === 'studio-login') return 'studio-login';
  if (stepKey.startsWith('signup-creator')) return 'signup-creator';
  if (stepKey.startsWith('signup')) return 'signup-member';
  if (stepKey === 'verify-email' || stepKey.includes('forgot-password-otp')) return 'verify-email';
  if (stepKey.includes('forgot-password-success')) return 'success';
  if (stepKey.includes('forgot-password-password')) return 'password-reset';
  if (stepKey.startsWith('forgot-password')) return 'forgot-password';
  if (stepKey.includes('creator-checkout')) return 'creator-checkout';
  if (stepKey.includes('creator-activating')) return 'creator-activating';
  if (stepKey.includes('creator-plan') || stepKey.includes('creator-creator')) return 'creator-plan';
  if (stepKey.startsWith('login')) return 'social-login';
  return 'social-login';
}

type PastelTone = 'sky' | 'mint' | 'peach' | 'lavender' | 'lemon';

const PASTEL_CARD: Record<PastelTone, string> = {
  sky: 'border border-pastel-sky-border bg-pastel-sky text-[hsl(220_12%_13%)]',
  mint: 'border border-pastel-mint-border bg-pastel-mint text-[hsl(220_12%_13%)]',
  peach: 'border border-pastel-peach-border bg-pastel-peach text-[hsl(220_12%_13%)]',
  lavender: 'border border-pastel-lavender-border bg-pastel-lavender text-[hsl(220_12%_13%)]',
  lemon: 'border border-pastel-lemon-border bg-pastel-lemon text-[hsl(220_12%_13%)]',
};

const PASTEL_VIVID: Record<PastelTone, string> = {
  sky: 'bg-tint-sky-vivid text-tint-sky-vivid-foreground',
  mint: 'bg-tint-mint-vivid text-tint-mint-vivid-foreground',
  peach: 'bg-tint-peach-vivid text-tint-peach-vivid-foreground',
  lavender: 'bg-tint-lavender-vivid text-tint-lavender-vivid-foreground',
  lemon: 'bg-tint-lemon-vivid text-tint-lemon-vivid-foreground',
};

function HeroBackdrop() {
  return <div className="absolute inset-0 bg-[hsl(220_12%_13%)]" aria-hidden />;
}

function PastelCard({
  tone,
  className,
  style,
  children,
}: {
  tone: PastelTone;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div className={cn('rounded-2xl p-3.5 shadow-md', PASTEL_CARD[tone], className)} style={style}>
      {children}
    </div>
  );
}

function Chip({ tone, children }: { tone: PastelTone; children: ReactNode }) {
  return (
    <span className={cn('rounded-md px-2 py-0.5 text-[10px] font-semibold', PASTEL_VIVID[tone])}>
      {children}
    </span>
  );
}

function FeedScrollVisual() {
  const posts: { tone: PastelTone; label: string }[] = [
    { tone: 'sky', label: 'New post' },
    { tone: 'lavender', label: 'Circle update' },
    { tone: 'mint', label: 'Course drop' },
    { tone: 'peach', label: 'Live event' },
    { tone: 'lemon', label: 'Article' },
  ];
  const loop = [...posts, ...posts];

  return (
    <div className="relative mx-auto h-[300px] w-full max-w-[300px]">
      <PastelCard
        tone="lavender"
        className="absolute -left-10 -top-3 z-20 w-28 rotate-[-8deg] p-2.5 motion-reduce:animate-none [animation:hero-float_6s_ease-in-out_infinite]"
      >
        <Chip tone="peach">128 likes</Chip>
      </PastelCard>
      <PastelCard
        tone="lemon"
        className="absolute -right-10 top-1 z-20 w-24 rotate-[6deg] p-2.5 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.4s' }}
      >
        <span className="text-[10px] font-semibold">Discover</span>
      </PastelCard>
      <PastelCard
        tone="mint"
        className="absolute -left-11 top-[38%] z-20 w-24 rotate-[-5deg] p-2.5 motion-reduce:animate-none [animation:hero-float_5.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.8s' }}
      >
        <span className="text-[10px] font-semibold">24 comments</span>
      </PastelCard>
      <PastelCard
        tone="sky"
        className="absolute -right-11 top-[52%] z-20 rotate-[7deg] p-2 motion-reduce:animate-none [animation:hero-float_6.5s_ease-in-out_infinite]"
        style={{ animationDelay: '1.1s' }}
      >
        <Chip tone="lavender">Trending</Chip>
      </PastelCard>
      <PastelCard
        tone="peach"
        className="absolute -bottom-2 -left-8 z-20 rotate-[-6deg] px-2.5 py-2 motion-reduce:animate-none [animation:hero-float_4.8s_ease-in-out_infinite]"
        style={{ animationDelay: '0.55s' }}
      >
        <span className="text-[10px] font-semibold">3 Circles</span>
      </PastelCard>
      <PastelCard
        tone="lavender"
        className="absolute -bottom-1 -right-9 z-20 rotate-[5deg] px-2.5 py-2 motion-reduce:animate-none [animation:hero-float_5.2s_ease-in-out_infinite]"
        style={{ animationDelay: '1.4s' }}
      >
        <Chip tone="mint">New follow</Chip>
      </PastelCard>
      <div className="relative z-10 mx-auto h-[260px] w-full max-w-[260px] overflow-hidden rounded-2xl border border-white/8 shadow-lg">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-[hsl(220_12%_13%)] to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-[hsl(220_12%_13%)] to-transparent" />
        <div className="flex flex-col gap-2.5 p-2.5 motion-reduce:[animation:none] [animation:feed-scroll-up_14s_linear_infinite]">
          {loop.map((post, i) => (
            <PastelCard key={`${post.label}-${i}`} tone={post.tone} className="shrink-0 p-3">
              <div className="flex items-center gap-2">
                <span className={cn('size-4 rounded-full', PASTEL_VIVID[post.tone])} />
                <span className="text-[11px] font-bold">{post.label}</span>
              </div>
              <div className="mt-2 space-y-1">
                <span className="block h-1 rounded-full bg-current/15" />
                <span className="block h-1 w-4/5 rounded-full bg-current/10" />
              </div>
            </PastelCard>
          ))}
        </div>
      </div>
    </div>
  );
}

function StudioBarsVisual() {
  const modules: { tone: PastelTone; label: string; h: number }[] = [
    { tone: 'sky', label: 'Analytics', h: 72 },
    { tone: 'mint', label: 'Products', h: 52 },
    { tone: 'lemon', label: 'Courses', h: 88 },
    { tone: 'peach', label: 'Events', h: 60 },
    { tone: 'lavender', label: 'Members', h: 76 },
  ];

  return (
    <div className="relative mx-auto w-full max-w-[280px]">
      <PastelCard tone="sky" className="absolute -right-3 top-0 z-20 rotate-[5deg] p-2">
        <Chip tone="mint">+24%</Chip>
      </PastelCard>
      <div className="flex items-end justify-center gap-2 pt-8">
        {modules.map((mod, i) => (
          <div key={mod.label} className="flex flex-col items-center gap-1.5">
            <PastelCard
              tone={mod.tone}
              className="flex w-11 items-end justify-center p-1.5 motion-reduce:animate-none [animation:hero-float_4s_ease-in-out_infinite]"
              style={{ height: `${mod.h}px`, animationDelay: `${i * 0.12}s` }}
            >
              <span className={cn('mb-1 block w-full rounded-sm', PASTEL_VIVID[mod.tone])} style={{ height: '55%' }} />
            </PastelCard>
            <span className="text-[9px] font-medium text-white/55">{mod.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OtpDotsVisual({ accent = 'sky' }: { accent?: 'sky' | 'lavender' }) {
  const mailTone: PastelTone = accent === 'lavender' ? 'lavender' : 'sky';
  const slotTone: PastelTone = accent === 'lavender' ? 'peach' : 'lemon';

  return (
    <div className="relative mx-auto flex w-full max-w-[260px] flex-col items-center gap-4">
      <PastelCard
        tone={mailTone}
        className="w-full motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <div className="flex items-center gap-2">
          <span className={cn('flex size-9 items-center justify-center rounded-xl', PASTEL_VIVID[mailTone])}>
            <svg viewBox="0 0 24 24" className="size-4 fill-none stroke-current stroke-[1.75]">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
          </span>
          <div>
            <p className="text-xs font-bold">Check your inbox</p>
            <p className="text-[10px] opacity-70">6-digit code</p>
          </div>
        </div>
      </PastelCard>
      <PastelCard tone={slotTone} className="w-full">
        <div className="flex justify-center gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className={cn(
                'size-7 rounded-lg motion-reduce:animate-none [animation:feature-pulse-ring_2.4s_ease-in-out_infinite]',
                i < 3 ? PASTEL_VIVID[slotTone] : 'bg-white/20',
              )}
              style={{ animationDelay: `${i * 0.18}s` }}
            />
          ))}
        </div>
      </PastelCard>
    </div>
  );
}

function ShieldVisual({ accent = 'mint' }: { accent?: 'mint' | 'lavender' }) {
  const primary: PastelTone = accent === 'lavender' ? 'lavender' : 'mint';
  const secondary: PastelTone = accent === 'lavender' ? 'peach' : 'sky';

  return (
    <div className="relative mx-auto flex size-44 items-center justify-center">
      <PastelCard
        tone={secondary}
        className="absolute -left-8 top-0 z-20 rotate-[-10deg] p-2 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <Chip tone={primary}>Secure</Chip>
      </PastelCard>
      <PastelCard
        tone={primary}
        className="relative z-10 flex size-24 items-center justify-center rounded-3xl p-0 motion-reduce:animate-none [animation:feature-pulse-ring_4s_ease-in-out_infinite]"
      >
        <svg viewBox="0 0 24 24" className="size-10 fill-none stroke-current stroke-[1.75]">
          <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      </PastelCard>
      <PastelCard
        tone="lemon"
        className="absolute -bottom-1 -right-8 z-20 rotate-[8deg] px-2 py-1.5 motion-reduce:animate-none [animation:hero-float_4.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.3s' }}
      >
        <span className="font-mono text-xs font-bold tracking-widest">••••••</span>
      </PastelCard>
    </div>
  );
}

function ProfileCardVisual() {
  const steps: { tone: PastelTone; label: string }[] = [
    { tone: 'sky', label: 'Pick @handle' },
    { tone: 'lemon', label: 'Verify email' },
    { tone: 'mint', label: 'Explore feed' },
  ];

  return (
    <div className="relative mx-auto min-h-[300px] w-full max-w-[300px] py-4">
      <PastelCard
        tone="mint"
        className="absolute -left-10 top-4 z-20 rotate-[-8deg] px-2.5 py-2 motion-reduce:animate-none [animation:hero-float_6s_ease-in-out_infinite]"
      >
        <Chip tone="mint">Free member</Chip>
      </PastelCard>
      <PastelCard
        tone="lavender"
        className="absolute -right-10 top-0 z-20 rotate-[7deg] px-2.5 py-2 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.35s' }}
      >
        <span className="text-[10px] font-semibold">Join Circles</span>
      </PastelCard>
      <PastelCard tone="sky" className="relative z-10 mx-auto max-w-[250px] p-4 shadow-lg">
        <p className="text-xs font-bold">Join loamify.io</p>
        <p className="mt-1 text-[10px] opacity-75">Claim your username to get started</p>
        <div className="mt-3 flex items-center gap-1.5 rounded-lg border border-pastel-sky-border bg-white/60 px-2.5 py-2">
          <span className="text-xs font-semibold text-[hsl(220_12%_13%)]/50">@</span>
          <span className="text-sm font-bold tracking-tight">yourname</span>
        </div>
        <ol className="mt-4 space-y-2">
          {steps.map((step, i) => (
            <li key={step.label} className="flex items-center gap-2">
              <span className={cn('flex size-5 shrink-0 items-center justify-center rounded-md text-[10px] font-bold', PASTEL_VIVID[step.tone])}>
                {i + 1}
              </span>
              <span className="text-[11px] font-medium">{step.label}</span>
            </li>
          ))}
        </ol>
      </PastelCard>
      <PastelCard
        tone="peach"
        className="absolute -bottom-1 -left-6 z-20 rotate-[-4deg] px-2.5 py-1.5 motion-reduce:animate-none [animation:hero-float_4.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.5s' }}
      >
        <span className="text-[10px] font-semibold">Follow creators</span>
      </PastelCard>
    </div>
  );
}

function CreatorPathVisual() {
  return (
    <div className="relative mx-auto flex w-full max-w-[260px] flex-col items-center gap-3">
      <PastelCard tone="sky" className="w-full motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]">
        <div className="flex items-center gap-2">
          <span className={cn('size-8 rounded-xl', PASTEL_VIVID.sky)} />
          <div>
            <p className="text-xs font-bold">loamify.io</p>
            <p className="text-[10px] opacity-70">Get discovered</p>
          </div>
        </div>
        <div className="mt-2 flex gap-1">
          <Chip tone="lavender">Feed</Chip>
          <Chip tone="peach">Articles</Chip>
        </div>
      </PastelCard>
      <div className="flex flex-col items-center gap-1 text-white/35">
        <span className="h-4 w-px bg-white/25" />
        <Chip tone="mint">+</Chip>
        <span className="h-4 w-px bg-white/25" />
      </div>
      <PastelCard
        tone="lemon"
        className="w-full motion-reduce:animate-none [animation:hero-float_4.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.35s' }}
      >
        <div className="flex items-center gap-2">
          <span className={cn('size-8 rounded-xl', PASTEL_VIVID.lemon)} />
          <div>
            <p className="text-xs font-bold">Loamify Studio</p>
            <p className="text-[10px] opacity-70">Run your business</p>
          </div>
        </div>
        <div className="mt-2 flex gap-1">
          <Chip tone="mint">Courses</Chip>
          <Chip tone="lavender">Sales</Chip>
        </div>
      </PastelCard>
    </div>
  );
}

function FoundingPlanVisual() {
  const {
    presaleAnnualUsd,
    listAnnualUsd,
    foundingPlatformFeePercent,
    foundingSlots,
  } = FOUNDING_PRICING;

  return (
    <div className="relative mx-auto min-h-[280px] w-full max-w-[300px] py-6">
      <PastelCard
        tone="peach"
        className="absolute -right-10 -top-1 z-20 rotate-[8deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <span className="text-[10px] font-bold">First {foundingSlots}</span>
      </PastelCard>
      <PastelCard
        tone="lemon"
        className="absolute -left-10 top-8 z-20 rotate-[-7deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_5.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.45s' }}
      >
        <Chip tone="mint">Annual</Chip>
      </PastelCard>
      <PastelCard
        tone="lavender"
        className="absolute -right-9 top-[42%] z-20 rotate-[6deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_4.8s_ease-in-out_infinite]"
        style={{ animationDelay: '0.75s' }}
      >
        <Chip tone="peach">Monthly</Chip>
      </PastelCard>
      <PastelCard tone="mint" className="relative z-10 mx-auto max-w-[230px] p-5 shadow-lg">
        <p className="text-[10px] font-bold uppercase tracking-wide opacity-70">Founding Studio</p>
        <p className="mt-2 text-4xl font-bold tracking-tight">${formatMarketingUsd(presaleAnnualUsd)}</p>
        <p className="mt-1 text-xs opacity-80">Pay 6 months · get 12</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          <Chip tone="lavender">{foundingPlatformFeePercent}% platform fee</Chip>
          <Chip tone="lemon">Priority access</Chip>
        </div>
      </PastelCard>
      <PastelCard
        tone="sky"
        className="absolute -bottom-2 -left-8 z-20 rotate-[-6deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_6s_ease-in-out_infinite]"
        style={{ animationDelay: '0.2s' }}
      >
        <span className="text-[10px] font-semibold line-through opacity-60">
          ${formatMarketingUsd(listAnnualUsd)}/yr
        </span>
      </PastelCard>
    </div>
  );
}

function CheckoutVisual() {
  return (
    <div className="relative mx-auto min-h-[240px] w-full max-w-[300px] py-4">
      <PastelCard
        tone="sky"
        className="absolute -left-9 top-3 z-20 rotate-[-6deg] p-2 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <Chip tone="lavender">Secure</Chip>
      </PastelCard>
      <PastelCard tone="lavender" className="relative z-10 mx-auto max-w-[230px] p-4 shadow-lg">
        <p className="text-xs font-bold">Founding checkout</p>
        <div className="mt-3 space-y-2">
          <div className="h-2 rounded-full bg-current/15" />
          <div className="h-2 w-3/4 rounded-full bg-current/10" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <Chip tone="sky">Stripe</Chip>
          <Chip tone="mint">Encrypted</Chip>
        </div>
      </PastelCard>
      <PastelCard
        tone="peach"
        className="absolute -bottom-3 -right-6 z-20 flex size-14 items-center justify-center rounded-2xl p-0 motion-reduce:animate-none [animation:feature-pulse-ring_3s_ease-in-out_infinite]"
      >
        <svg viewBox="0 0 24 24" className="size-6 fill-none stroke-current stroke-2">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 118 0v4" />
        </svg>
      </PastelCard>
    </div>
  );
}

function ActivatingVisual() {
  const orbit: PastelTone[] = ['sky', 'mint', 'peach', 'lavender', 'lemon'];
  return (
    <div className="relative mx-auto flex size-44 items-center justify-center">
      {orbit.map((tone, i) => {
        const angle = (i / orbit.length) * Math.PI * 2 - Math.PI / 2;
        const r = 68;
        return (
          <PastelCard
            key={tone}
            tone={tone}
            className="absolute size-10 rounded-xl p-0 motion-reduce:animate-none [animation:hero-float_4s_ease-in-out_infinite]"
            style={{
              left: `calc(50% + ${Math.cos(angle) * r}px - 1.25rem)`,
              top: `calc(50% + ${Math.sin(angle) * r}px - 1.25rem)`,
              animationDelay: `${i * 0.15}s`,
            }}
          />
        );
      })}
      <PastelCard tone="lemon" className="relative z-10 flex size-16 items-center justify-center rounded-2xl p-0">
        <div className="size-8 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />
      </PastelCard>
    </div>
  );
}

function AdminGridVisual() {
  const tiles: { tone: PastelTone; label: string }[] = [
    { tone: 'sky', label: 'Users' },
    { tone: 'mint', label: 'Mail' },
    { tone: 'peach', label: 'Flags' },
    { tone: 'lavender', label: 'Legal' },
    { tone: 'lemon', label: 'Audit' },
    { tone: 'sky', label: 'Ops' },
  ];

  return (
    <div className="grid grid-cols-3 gap-2.5">
      {tiles.map((tile, i) => (
        <PastelCard
          key={tile.label}
          tone={tile.tone}
          className="flex aspect-square flex-col items-center justify-center gap-1 p-2 motion-reduce:animate-none [animation:hero-float_4s_ease-in-out_infinite]"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <span className={cn('size-5 rounded-md', PASTEL_VIVID[tile.tone])} />
          <span className="text-[9px] font-bold">{tile.label}</span>
        </PastelCard>
      ))}
    </div>
  );
}

function SuccessVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[220px]">
      <PastelCard
        tone="mint"
        className="flex flex-col items-center py-6 motion-reduce:animate-none [animation:feature-pulse-ring_3s_ease-in-out_infinite]"
      >
        <span className={cn('flex size-16 items-center justify-center rounded-2xl', PASTEL_VIVID.mint)}>
          <svg viewBox="0 0 24 24" className="size-8 fill-none stroke-current stroke-[2.5]">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <p className="mt-3 text-sm font-bold">All set</p>
      </PastelCard>
      <PastelCard
        tone="lemon"
        className="absolute -bottom-3 -right-2 rotate-[6deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <span className="text-[10px] font-semibold">Welcome back</span>
      </PastelCard>
    </div>
  );
}

function KeyVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[240px]">
      <PastelCard
        tone="peach"
        className="flex items-center gap-3 motion-reduce:animate-none [animation:hero-float_5s_ease-in-out_infinite]"
      >
        <span className={cn('flex size-12 items-center justify-center rounded-xl', PASTEL_VIVID.peach)}>
          <svg viewBox="0 0 24 24" className="size-6 fill-none stroke-current stroke-[1.75]">
            <circle cx="8" cy="8" r="4" />
            <path d="M11 11l9 9M16 16l-2 2M19 13l-2 2" />
          </svg>
        </span>
        <div>
          <p className="text-xs font-bold">Account recovery</p>
          <p className="text-[10px] opacity-70">We&apos;ll send a code</p>
        </div>
      </PastelCard>
      <PastelCard
        tone="sky"
        className="absolute -bottom-4 left-4 rotate-[-5deg] px-2 py-1 motion-reduce:animate-none [animation:hero-float_4.5s_ease-in-out_infinite]"
        style={{ animationDelay: '0.3s' }}
      >
        <Chip tone="lavender">Secure reset</Chip>
      </PastelCard>
    </div>
  );
}

function VisualForVariant({ variant }: { variant: AuthHeroVariant }) {
  switch (variant) {
    case 'studio-login':
      return <StudioBarsVisual />;
    case 'two-factor':
      return <ShieldVisual />;
    case 'admin-2fa':
      return <ShieldVisual accent="lavender" />;
    case 'signup-member':
      return <ProfileCardVisual />;
    case 'signup-creator':
      return <CreatorPathVisual />;
    case 'verify-email':
      return <OtpDotsVisual />;
    case 'admin-verify':
      return <OtpDotsVisual accent="lavender" />;
    case 'forgot-password':
      return <KeyVisual />;
    case 'password-reset':
      return <ShieldVisual accent="lavender" />;
    case 'success':
      return <SuccessVisual />;
    case 'creator-plan':
      return <FoundingPlanVisual />;
    case 'creator-checkout':
      return <CheckoutVisual />;
    case 'creator-activating':
      return <ActivatingVisual />;
    case 'admin-login':
      return <AdminGridVisual />;
    case 'social-login':
    default:
      return <FeedScrollVisual />;
  }
}

export function AuthHeroVisual({ variant, className }: { variant: AuthHeroVariant; className?: string }) {
  return (
    <div className={cn('relative flex w-full items-center justify-center px-6', className)}>
      <div key={variant} className="auth-hero-visual-enter w-full max-w-[300px]">
        <VisualForVariant variant={variant} />
      </div>
    </div>
  );
}

export function AuthHeroPanel({
  variant,
  heroTitle,
  heroSubtitle,
}: {
  variant: AuthHeroVariant;
  heroTitle: string;
  heroSubtitle?: string;
}) {
  return (
    <>
      <HeroBackdrop />
      <div
        data-surface="auth-hero"
        className="relative z-10 flex min-h-dvh flex-col justify-between px-8 py-12"
      >
        <div className="flex flex-1 items-center justify-center py-6">
          <AuthHeroVisual variant={variant} />
        </div>
        <div className="shrink-0 text-center">
          <h2 className="text-balance text-xl font-semibold tracking-tight text-white sm:text-2xl">{heroTitle}</h2>
          {heroSubtitle ? (
            <p className="mt-2 text-pretty text-sm leading-relaxed text-white/65">{heroSubtitle}</p>
          ) : null}
        </div>
      </div>
    </>
  );
}
